<!--
  PortalButton -- the ONE shared Client Portal action button (D6-polish).

  Why this exists
  ===============
  Portal button styling had been done ad-hoc across ~17 portal .vue files
  (each page opting into `rounded="pill"` on a raw v-btn, and repeatedly
  MISSING it -- Orders had it, then Approvals shipped rectangular). This
  component bakes the v2 pill radius IN so a page cannot ship a rectangular
  portal button by construction: use PortalButton and it is a pill.

  Design source: planning .../2026-06-24-client-portal-v2/design/primitives.jsx
  -- the `Btn` primitive (borderRadius: fx.radius.pill; variants primary /
  secondary / ghost; a `sm` size; an icon slot; brand-driven colors via CSS
  vars so a white-labelled client re-themes at runtime).

  Tokens
  ======
  Colors read the same `--tf-*` / `--portal-brand-*` custom properties the
  rest of the portal chrome uses (with literal fallbacks so the button still
  renders correctly in the test env / before the token sheet loads). The pill
  radius carries an inline `9999px` fallback for the same reason.

  API
  ===
    <PortalButton variant="primary|secondary|ghost" size="sm"
                  :disabled :loading :block type="button|submit"
                  @click="...">
      <template #icon><v-icon size="18">check</v-icon></template>
      Approve proof
    </PortalButton>
-->
<template>
    <button
        :type="type"
        class="portal-btn portal-btn--pill"
        :class="[
            `portal-btn--${variant}`,
            { 'portal-btn--sm': size === 'sm', 'portal-btn--block': block },
        ]"
        :style="{ borderRadius: '9999px' }"
        :disabled="disabled || loading"
        :aria-busy="loading ? 'true' : null"
        @click="onClick"
    >
        <span
            v-if="loading"
            class="portal-btn__spinner"
            aria-hidden="true"
        ></span>
        <span
            v-else-if="$slots.icon"
            class="portal-btn__icon"
        ><slot name="icon" /></span>
        <span class="portal-btn__label"><slot /></span>
    </button>
</template>

<script>
export default {
    name: "PortalButton",
    props: {
        // primary (brand fill) | secondary (brand-ink outline) | ghost
        // (neutral outline). Mirrors the design Btn `kind`.
        variant: {
            type: String,
            default: "primary",
            validator: (v) => ["primary", "secondary", "ghost"].includes(v),
        },
        // "sm" tightens padding + type for dense rows (design `size: 'sm'`).
        size: {
            type: String,
            default: "md",
            validator: (v) => ["sm", "md"].includes(v),
        },
        disabled: { type: Boolean, default: false },
        // While loading the button is disabled and shows a spinner in place of
        // the icon (mirrors v-btn :loading so callers migrate 1:1).
        loading: { type: Boolean, default: false },
        // Full-width (design Btn `full`).
        block: { type: Boolean, default: false },
        type: {
            type: String,
            default: "button",
            validator: (v) => ["button", "submit", "reset"].includes(v),
        },
    },
    emits: ["click"],
    methods: {
        onClick(event) {
            // Belt-and-suspenders: `disabled` already blocks native clicks,
            // but a `loading` overlay or pointer-events edge case should never
            // double-fire an action.
            if (this.disabled || this.loading) return;
            this.$emit("click", event);
        },
    },
};
</script>

<style scoped>
.portal-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 17px;
    font-size: 14px;
    font-weight: 500;
    font-family: inherit;
    line-height: 1.2;
    min-height: 40px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: filter 0.12s, background 0.12s;
    /* The pill is baked in -- callers never opt in. The inline style binding
       provides a literal fallback for environments that do not resolve the
       CSS var (tests / SSR); the token wins when the sheet is loaded. */
    border-radius: var(--tf-radius-pill, 9999px);
}
.portal-btn:hover:not(:disabled) {
    filter: brightness(0.94);
}
.portal-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.portal-btn--block {
    width: 100%;
}
.portal-btn--sm {
    padding: 7px 13px;
    font-size: 13px;
    min-height: 34px;
}

/* ---- Variants (design Btn `kinds`) ---- */
.portal-btn--primary {
    background: var(--portal-brand-color, var(--tf-brand, #1565c0));
    color: var(--tf-on-brand, #ffffff);
}
.portal-btn--secondary {
    background: var(--tf-surface, #ffffff);
    color: var(--tf-brand-ink, #1565c0);
    border: 1px solid var(--tf-brand-soft-bd, var(--tf-border, #e0e0e0));
}
.portal-btn--ghost {
    background: transparent;
    color: var(--tf-fg-2, #424242);
    border: 1px solid var(--tf-border, #e0e0e0);
}

.portal-btn__icon {
    display: inline-flex;
    align-items: center;
}
.portal-btn__label {
    display: inline-flex;
    align-items: center;
}

/* Minimal spinner for the loading state (no Vuetify dependency). */
.portal-btn__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: portal-btn-spin 0.7s linear infinite;
}
@keyframes portal-btn-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
